import { useEffect, useState, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Search, User, Car, FileText, AlertOctagon, Calendar, X, Command as CommandIcon } from "lucide-react";
import { api } from "../lib/api";

/**
 * CommandPalette — Spotlight-style global search (Cmd+K / Ctrl+K).
 * Cerca trasversalmente: clienti, veicoli, contratti, multe, prenotazioni.
 * Aperto da:
 *  - Scorciatoia tastiera: Cmd+K (Mac) o Ctrl+K (Windows/Linux)
 *  - Click sul pulsante 🔍 nell'header (per mobile/touch)
 *
 * Click su un risultato → naviga alla pagina giusta col record selezionato.
 */
export default function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [data, setData] = useState({ customers: [], cars: [], contracts: [], multe: [], bookings: [] });
  const [loaded, setLoaded] = useState(false);
  const [selected, setSelected] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const navigate = useNavigate();

  // Carica dati lazy alla prima apertura
  useEffect(() => {
    if (!open || loaded) return;
    Promise.all([
      api.get("/customers?limit=500").catch(() => ({ data: [] })),
      api.get("/cars").catch(() => ({ data: [] })),
      api.get("/contracts").catch(() => ({ data: [] })),
      api.get("/multe").catch(() => ({ data: [] })),
      api.get("/bookings").catch(() => ({ data: [] })),
    ]).then(([cu, c, ct, m, b]) => {
      setData({ customers: cu.data, cars: c.data, contracts: ct.data, multe: m.data, bookings: b.data });
      setLoaded(true);
    });
  }, [open, loaded]);

  // Refresh dati ogni volta che si riapre (per avere dati freschi dopo modifiche)
  useEffect(() => { if (!open) setQuery(""); }, [open]);

  // Scorciatoia globale Cmd+K / Ctrl+K
  useEffect(() => {
    const h = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((v) => !v);
      } else if (e.key === "Escape" && open) {
        setOpen(false);
      }
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [open]);

  // Espone API globale per aprire la palette da altri componenti (es. header search button)
  useEffect(() => {
    window.__openCommandPalette = () => setOpen(true);
    return () => { delete window.__openCommandPalette; };
  }, []);

  // Focus input quando si apre
  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 30);
  }, [open]);

  // Costruisci risultati unificati
  const results = useMemo(() => {
    if (!query.trim()) {
      // Senza query, mostra suggerimenti rapidi (top 3 di ciascuna categoria)
      return [
        ...data.customers.slice(0, 3).map(asCustomer),
        ...data.cars.slice(0, 3).map(asCar),
        ...data.contracts.slice(0, 3).map(asContract),
      ];
    }
    const q = query.toLowerCase().trim();
    const matchCust = data.customers.filter((c) =>
      [c.nome, c.cognome, c.ragione_sociale, c.codice_fiscale, c.piva, c.email, c.telefono]
        .some((x) => (x || "").toLowerCase().includes(q))
    ).slice(0, 8).map(asCustomer);
    const matchCar = data.cars.filter((c) =>
      [c.targa, c.marca, c.modello, c.telaio].some((x) => (x || "").toLowerCase().includes(q))
    ).slice(0, 8).map(asCar);
    const matchCtr = data.contracts.filter((c) =>
      [c.numero, c.car?.targa, c.customer?.ragione_sociale,
        `${c.customer?.nome || ""} ${c.customer?.cognome || ""}`]
        .some((x) => (x || "").toLowerCase().includes(q))
    ).slice(0, 8).map(asContract);
    const matchMul = data.multe.filter((m) =>
      [m.numero_verbale, m.targa, m.ente_verbalizzante].some((x) => (x || "").toLowerCase().includes(q))
    ).slice(0, 6).map(asMulta);
    const matchBoo = data.bookings.filter((b) =>
      [b.numero, b.car?.targa, b.customer?.email].some((x) => (x || "").toLowerCase().includes(q))
    ).slice(0, 6).map(asBooking);
    return [...matchCust, ...matchCar, ...matchCtr, ...matchMul, ...matchBoo];
  }, [query, data]);

  // Reset selected quando cambiano risultati
  useEffect(() => { setSelected(0); }, [query, results.length]);

  // Keyboard nav nei risultati
  useEffect(() => {
    if (!open) return;
    const h = (e) => {
      if (e.key === "ArrowDown") { e.preventDefault(); setSelected((s) => Math.min(s + 1, results.length - 1)); }
      else if (e.key === "ArrowUp") { e.preventDefault(); setSelected((s) => Math.max(s - 1, 0)); }
      else if (e.key === "Enter" && results[selected]) {
        e.preventDefault();
        goTo(results[selected]);
      }
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [open, results, selected]);

  // Scroll item in view
  useEffect(() => {
    if (!open || !listRef.current) return;
    const el = listRef.current.querySelector(`[data-cmd-idx="${selected}"]`);
    if (el) el.scrollIntoView({ block: "nearest" });
  }, [selected, open]);

  const goTo = (item) => {
    setOpen(false);
    navigate(item.url);
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-md flex items-start justify-center pt-[10vh] px-4 sm:px-6"
      onClick={() => setOpen(false)}
      data-testid="command-palette"
    >
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-2xl bg-[#0a0a0a]/98 border border-white/15 rounded-2xl shadow-2xl shadow-black/80 overflow-hidden">
        {/* Input */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-white/10">
          <Search size={18} className="text-white/40 flex-shrink-0" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Cerca clienti, veicoli, contratti, multe, prenotazioni…"
            className="flex-1 bg-transparent outline-none text-sm text-white/90 placeholder:text-white/35"
            data-testid="command-palette-input"
          />
          <kbd className="hidden sm:inline-flex items-center gap-1 text-[10px] text-white/40 bg-white/[0.04] px-1.5 py-0.5 rounded border border-white/10">ESC</kbd>
          <button onClick={() => setOpen(false)} className="sm:hidden text-white/40 hover:text-white"><X size={16} /></button>
        </div>

        {/* Risultati */}
        <div ref={listRef} className="max-h-[60vh] overflow-y-auto">
          {!loaded ? (
            <div className="px-6 py-10 text-center text-white/40 text-sm">Caricamento…</div>
          ) : results.length === 0 ? (
            <div className="px-6 py-10 text-center">
              <Search size={28} className="mx-auto text-white/20 mb-2" />
              <div className="text-white/50 text-sm">Nessun risultato per "<b className="text-white/80">{query}</b>"</div>
              <div className="text-white/30 text-xs mt-1">Prova nome cliente, targa, numero contratto, verbale…</div>
            </div>
          ) : (
            <>
              {!query.trim() && (
                <div className="px-4 py-2 text-[10px] uppercase tracking-widest text-white/35 border-b border-white/5 bg-white/[0.02]">
                  Suggerimenti recenti
                </div>
              )}
              {results.map((item, idx) => {
                const active = idx === selected;
                const Icon = item.icon;
                return (
                  <button
                    key={`${item.kind}-${item.id}`}
                    data-cmd-idx={idx}
                    data-testid={`command-result-${item.kind}-${item.id}`}
                    onClick={() => goTo(item)}
                    onMouseEnter={() => setSelected(idx)}
                    className={`w-full text-left px-4 py-2.5 flex items-center gap-3 transition border-b border-white/[0.03] last:border-0 ${active ? "bg-[#ff0033]/15" : "hover:bg-white/[0.03]"}`}
                  >
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${item.iconCls}`}>
                      <Icon size={14} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <div className="text-sm text-white/90 truncate">{item.title}</div>
                        <span className={`text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-full border ${item.kindCls}`}>{item.kindLabel}</span>
                      </div>
                      <div className="text-[11px] text-white/40 truncate">{item.subtitle}</div>
                    </div>
                    {active && (
                      <kbd className="hidden sm:inline-flex items-center gap-1 text-[10px] text-white/45 bg-white/[0.05] px-1.5 py-0.5 rounded border border-white/10">↵</kbd>
                    )}
                  </button>
                );
              })}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="hidden sm:flex items-center justify-between px-4 py-2 text-[10px] text-white/40 border-t border-white/5 bg-white/[0.02]">
          <div className="flex items-center gap-3">
            <span><kbd className="bg-white/[0.05] px-1.5 py-0.5 rounded border border-white/10 mr-1">↑↓</kbd>naviga</span>
            <span><kbd className="bg-white/[0.05] px-1.5 py-0.5 rounded border border-white/10 mr-1">↵</kbd>apri</span>
            <span><kbd className="bg-white/[0.05] px-1.5 py-0.5 rounded border border-white/10 mr-1">ESC</kbd>chiudi</span>
          </div>
          <div className="flex items-center gap-1">
            <CommandIcon size={10} /> + K per riaprire
          </div>
        </div>
      </div>
    </div>
  );
}

// ===== Result mappers — convertono le entità in oggetti uniformi =====

function asCustomer(c) {
  const name = c.ragione_sociale || `${c.nome || ""} ${c.cognome || ""}`.trim() || "(senza nome)";
  return {
    kind: "customer", kindLabel: "Cliente", kindCls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
    id: c.id, title: name,
    subtitle: [c.codice_fiscale || c.piva, c.email, c.telefono].filter(Boolean).join(" · "),
    icon: User, iconCls: "bg-emerald-500/15 text-emerald-300",
    url: `/admin/customers?q=${encodeURIComponent(name)}`,
  };
}
function asCar(c) {
  return {
    kind: "car", kindLabel: "Veicolo", kindCls: "bg-blue-500/15 text-blue-300 border-blue-500/30",
    id: c.id, title: `${c.marca} ${c.modello}`,
    subtitle: `${c.targa} · ${c.carburante || ""} · ${c.anno_immatricolazione || c.anno || ""}`,
    icon: Car, iconCls: "bg-blue-500/15 text-blue-300",
    url: `/admin/fleet?q=${encodeURIComponent(c.targa)}`,
  };
}
function asContract(c) {
  const cli = c.customer?.ragione_sociale || `${c.customer?.nome || ""} ${c.customer?.cognome || ""}`.trim() || "—";
  return {
    kind: "contract", kindLabel: "Contratto", kindCls: "bg-[#ff0033]/15 text-[#ff5577] border-[#ff0033]/30",
    id: c.id, title: `Contratto ${c.numero}`,
    subtitle: `${cli} · ${c.car?.targa || ""} · ${c.data_inizio} → ${c.data_fine}`,
    icon: FileText, iconCls: "bg-[#ff0033]/15 text-[#ff5577]",
    url: `/admin/contracts?q=${encodeURIComponent(c.numero)}`,
  };
}
function asMulta(m) {
  return {
    kind: "multa", kindLabel: "Multa", kindCls: "bg-amber-500/15 text-amber-300 border-amber-500/30",
    id: m.id, title: `Verbale ${m.numero_verbale}`,
    subtitle: `${m.targa} · ${m.ente_verbalizzante || ""} · €${m.importo || 0}`,
    icon: AlertOctagon, iconCls: "bg-amber-500/15 text-amber-300",
    url: `/admin/multe?q=${encodeURIComponent(m.numero_verbale)}`,
  };
}
function asBooking(b) {
  const cli = b.customer?.ragione_sociale || `${b.customer?.nome || ""} ${b.customer?.cognome || ""}`.trim() || "—";
  return {
    kind: "booking", kindLabel: "Prenotazione", kindCls: "bg-purple-500/15 text-purple-300 border-purple-500/30",
    id: b.id, title: b.numero || "Prenotazione",
    subtitle: `${cli} · ${b.car?.targa || ""} · ${b.data_inizio} → ${b.data_fine}`,
    icon: Calendar, iconCls: "bg-purple-500/15 text-purple-300",
    url: `/admin/bookings`,
  };
}
